import TeamSchedule from "@webV2/components/TeamSchedule";
import React, { useMemo } from "react";
import { AgendaWeek } from "@webV2/components/dashboard/AgendaWeek";
import { useDashboardData } from "@webV2/dashboard/useDashboardData";
import { MobileContentLayoutContainer } from "@webV2/mobile/MobileContentLayoutContainer";
import { MobileDivisionSelector } from "@v2/components/divisions/DivisionSelector/MobileDivisionSelector";
import { Tabs, TabsContent } from "@v2/components/ui/tabs";
import { DashboardTabsList } from "@webV2/dashboard/partial/DashboardTabsList";
import { Visitors } from "@webV2/components/dashboard-subpages/Visitors";
import { News } from "@webV2/components/dashboard-subpages/News";
import { SiteInfo } from "@webV2/components/dashboard-subpages/SiteInfo";
import { DashboardEventDetails } from "@webV2/components/dashboard/DashboardEventDetails/DashboardEventDetails";
import { getDefaultDashboardTab, useDisclosure } from "@gettactic/helpers";
import { MobileDrawer } from "@v2/mobile/MobileDrawer/MobileDrawer";
import {
  WorkplaceRequestModalProvider,
  useWorkplaceRequestModal
} from "../contexts/WorkplaceRequestModalContext";
import { RequestDetailsPage } from "@webV2/pages/settings/WorkplaceRequestsPage/components/RequestDetailsPage";
import { WorkplaceRequestElement } from "@gettactic/api";
import { useSettings } from "@gettactic/hooks";
import { PageOptions } from "@/contexts/DashboardStateContext";
import { Button } from "@v2/components/ui/button";
import { OOORequest } from "@/components/attendance/employee/OOORequest";
import { ConfirmModal } from "@cmp-src/overlay/modals/ConfirmModal";

function MobileDashboardContent() {
  const { isOrgPrivacyEnabled } = useSettings();
  const defaultTab: PageOptions = useMemo(
    () => getDefaultDashboardTab(isOrgPrivacyEnabled, true),
    [isOrgPrivacyEnabled]
  );
  const {
    shouldRenderContent,
    activeTab,
    handleTabChange,
    setOffices,
    selectedEvent,
    setSelectedEvent,
    isAdmin,
    hasPages,
    nextWeek,
    prevWeek,
    currentOffice,
    locationData,
    finalDaysCount,
    onConfirmDialogCallback,
    setOnConfirmDialogCallback,
    oooRequest,
    setOooRequest,
    setPage
  } = useDashboardData(
    // on Mobile we always show Attendance tab
    // because user's own info is also on it
    { defaultTab }
  );

  const nowDay = new Date().getDay();
  const [selectedDayIndex, setSelectedDayIndex] = React.useState<number>(
    nowDay === 0 ? 6 : nowDay - 1
  );
  const eventDetailsDisclosure = useDisclosure();
  const {
    isOpen: workplaceRequestsModalOpen,
    selectedRequest,
    closeModal
  } = useWorkplaceRequestModal();

  React.useEffect(() => {
    if (selectedEvent) {
      eventDetailsDisclosure.onOpen();
    }
  }, [selectedEvent]);

  if (!shouldRenderContent) {
    return null; // Return null until router is ready to prevent hooks from being conditionally rendered
  }

  return (
    <>
      <MobileContentLayoutContainer cleanWrapper>
        <Tabs
          defaultValue="schedules"
          value={activeTab}
          onValueChange={handleTabChange}
          className={"flex w-screen flex-col gap-4 px-2"}
        >
          <div className={"mt-2 flex items-center gap-2 pl-2"}>
            <div className={"min-w-0 flex-1"}>
              <MobileDivisionSelector
                selectedOffice={currentOffice}
                onSelectOffice={(office) => {
                  if (!office) return;
                  setOffices(office.id);
                }}
              />
            </div>
            <div>
              <DashboardTabsList
                hasPages={hasPages}
                isAdmin={isAdmin}
                selectedEvent={selectedEvent}
                setSelectedEvent={setSelectedEvent}
                isMobile={true}
                showAttendanceTab={!isOrgPrivacyEnabled}
                setPage={setPage}
                defaultPage={defaultTab}
              />
            </div>
          </div>
          <TabsContent value="schedules" asChild>
            <div className={"flex flex-col gap-2"}>
              <AgendaWeek
                daysCount={finalDaysCount + 1}
                locationData={locationData}
                selectedDayIndex={selectedDayIndex}
                setSelectedDayIndex={setSelectedDayIndex}
                onNextWeek={nextWeek}
                onPrevWeek={prevWeek}
                showAsMobileDaily
              />
              {!isOrgPrivacyEnabled && (
                <TeamSchedule
                  daysCount={finalDaysCount}
                  selectedDayIndex={selectedDayIndex}
                  showAsMobileDaily
                />
              )}
            </div>
          </TabsContent>

          <TabsContent value="visitors" asChild>
            <div>
              <Visitors />
            </div>
          </TabsContent>

          <TabsContent value="news" asChild>
            <div>
              <News />
            </div>
          </TabsContent>
          <TabsContent value="siteInfo" asChild>
            <div>
              <SiteInfo />
            </div>
          </TabsContent>
        </Tabs>
      </MobileContentLayoutContainer>

      {oooRequest ? (
        <OOORequest
          onClose={() => {
            setOooRequest(null);
            locationData.invalidateQueries();
          }}
          request={oooRequest}
        />
      ) : null}
      {onConfirmDialogCallback ? (
        <ConfirmModal
          message={
            <p className="text-gray-500 text-sm">
              If you confirm the current reservations on this day will be
              cancelled
            </p>
          }
          onConfirm={onConfirmDialogCallback}
          title={
            <span className="font-bold text-2xl text-parent">
              Do you want to cancel?
            </span>
          }
          onOpenChange={(isOpen) => !isOpen && setOnConfirmDialogCallback(null)}
          isOpen={!!onConfirmDialogCallback}
        />
      ) : null}

      <MobileDrawer
        isOpen={workplaceRequestsModalOpen}
        onOpenChange={closeModal}
        scrollableContent={true}
      >
        <RequestDetailsPage
          requestId={selectedRequest?.id}
          isAdmin={isAdmin}
          onRequestUpdate={(updatedRequest: WorkplaceRequestElement) => {
            closeModal();
          }}
          onClose={closeModal}
          initialRequest={selectedRequest}
          dontTouchUrl={true}
          isPageMode={true}
          isInMobileDrawer={true}
        />
      </MobileDrawer>
      <MobileDrawer
        isOpen={eventDetailsDisclosure.isOpen}
        size={"fit"}
        onOpenChange={eventDetailsDisclosure.onOpenChange}
        onVisibleChange={(isVisible, isOpen) => {
          if (!isVisible && !isOpen) setSelectedEvent(null);
        }}
        below={
          <div className={"w-full"}>
            <Button
              className={"w-full"}
              variant={"secondary"}
              onClick={eventDetailsDisclosure.close}
            >
              Cancel
            </Button>
            <div className={"h-4"} />
          </div>
        }
        scrollableContent
        contentUnderGrabber
      >
        {selectedEvent && (
          <DashboardEventDetails
            schedule={selectedEvent}
            onCancel={eventDetailsDisclosure.close}
            isOrgPrivacyEnabled={isOrgPrivacyEnabled}
            teamSchedule={
              !isOrgPrivacyEnabled && (
                <div className={"mb-4 flex flex-col gap-2"}>
                  <TeamSchedule
                    daysCount={finalDaysCount}
                    selectedDayIndex={selectedDayIndex}
                    showAsMobileDaily
                  />
                </div>
              )
            }
          />
        )}
      </MobileDrawer>
    </>
  );
}

export function MobileDashboard() {
  return (
    <WorkplaceRequestModalProvider>
      <MobileDashboardContent />
    </WorkplaceRequestModalProvider>
  );
}
